"use client";

import Link from "next/link";
import { getAiSummaryContent } from "~/lib/siteglue/ai-summary-content";

/** Accessible, crawler-friendly SiteGlue AI Summary HTML page. */
export function AiSummaryPageContent() {
  const c = getAiSummaryContent();

  return (
    <div className="pb-16 pt-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <nav
          className="mb-8 flex items-center gap-2 text-sm text-muted-foreground"
          data-testid="ai-summary-breadcrumb"
          aria-label="Breadcrumb"
        >
          <Link href="/" className="transition-colors hover:text-foreground">
            Home
          </Link>
          <span aria-hidden>/</span>
          <span className="text-foreground">AI Summary</span>
        </nav>

        <article className="space-y-12">
          <header>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              {c.hero.eyebrow}
            </p>
            <h1
              className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl"
              data-testid="heading-ai-summary"
            >
              {c.hero.headline}
            </h1>
            <p className="text-sm text-muted-foreground">{c.hero.description}</p>
            <p className="mt-3 text-sm text-muted-foreground">
              Machine-readable versions:{" "}
              <a href="/ai-summary.md" className="text-primary hover:underline">
                /ai-summary.md
              </a>{" "}
              and{" "}
              <a href="/llms.txt" className="text-primary hover:underline">
                /llms.txt
              </a>
              . Last updated:{" "}
              <time dateTime="2026-07">{c.lastUpdated}</time>.
            </p>
          </header>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-what-is">
              What SiteGlue Is
            </h2>
            <p className="mb-3 leading-relaxed text-muted-foreground" data-testid="text-what-is">
              {c.product.description}
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {c.product.alternateIndustryTerm}
            </p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-positioning">
              Product Positioning
            </h2>
            <ol className="space-y-3">
              {c.product.positioning.map((line, i) => (
                <li key={line} className="rounded-lg border border-border bg-card p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                    {i === 0 ? "Primary" : i === 1 ? "Supporting" : "Differentiator"}
                  </p>
                  <p className="mt-1 font-medium text-foreground">{line}</p>
                </li>
              ))}
            </ol>
            <p className="mt-4 text-sm text-muted-foreground">{c.product.futureVision}</p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-hitl">
              Human-in-the-Loop™
            </h2>
            <p className="mb-4 leading-relaxed text-muted-foreground">
              {c.humanInTheLoop.definition}
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { title: "Team Handoff™", body: c.humanInTheLoop.humanHandoff },
                {
                  title: "Team Takeover™",
                  body: c.humanInTheLoop.teamMemberTakeover,
                },
                { title: "Human Approval", body: c.humanInTheLoop.humanApproval },
              ].map((item) => (
                <div key={item.title} className="rounded-lg border border-border bg-card p-4">
                  <h3 className="mb-2 text-sm font-semibold">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.body}</p>
                </div>
              ))}
            </div>
            <h3 className="mb-3 mt-6 text-lg font-medium">Team Takeover™ by channel</h3>
            <ul className="space-y-2">
              {c.humanInTheLoop.takeoverByChannel.map((row) => (
                <li
                  key={row.channel}
                  className="flex flex-col gap-0.5 rounded-lg border border-border px-4 py-3 sm:flex-row sm:gap-3"
                >
                  <span className="shrink-0 text-sm font-semibold text-foreground">
                    {row.channel}
                  </span>
                  <span className="text-sm text-muted-foreground">{row.behavior}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-trish">
              Trish AI Solutions Consultant
            </h2>
            <p className="mb-4 leading-relaxed text-muted-foreground">{c.trish.description}</p>
            <ul className="mb-4 grid gap-2 sm:grid-cols-2">
              {c.trish.capabilities.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mb-4 text-sm text-muted-foreground">
              Workflow: {c.trish.workflow.join(" → ")}
            </p>
            <a
              href={c.trish.onboardingUrl}
              className="inline-flex text-sm font-semibold text-primary hover:underline"
              rel="noopener noreferrer"
              target="_blank"
            >
              {c.trish.onboardingCta}
            </a>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">AI Opportunity Report</h2>
            <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
              {c.opportunityReport.intro}
            </p>
            <div className="mb-3 flex flex-wrap gap-2">
              {c.opportunityReport.sections.map((item) => (
                <span
                  key={item}
                  className="rounded-md border border-border px-2.5 py-1 text-xs font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{c.opportunityReport.estimateNote}</p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Monthly AI Impact Report</h2>
            <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
              {c.monthlyImpactReport.intro}
            </p>
            <div className="mb-3 flex flex-wrap gap-2">
              {c.monthlyImpactReport.sections.map((item) => (
                <span
                  key={item}
                  className="rounded-md bg-muted px-2.5 py-1 text-xs font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{c.monthlyImpactReport.upgradeNote}</p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-roles">
              AI Employee Roles
            </h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Explore{" "}
              <Link href={c.links.gallery.href} className="text-primary hover:underline">
                {c.ctas.exploreEmployees}
              </Link>{" "}
              or open a role page:
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {c.roles.map((role) => (
                <Link
                  key={role.slug}
                  href={role.href}
                  className="rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/50"
                >
                  <h3 className="text-sm font-semibold text-primary">{role.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{role.description}</p>
                </Link>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-channels">
              Communication Channels
            </h2>
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              {c.channels.map((ch) => (
                <div
                  key={ch.label}
                  className="rounded-lg border border-border bg-card p-4"
                >
                  <p className="text-sm font-semibold text-foreground">{ch.label}</p>
                  {ch.note ? (
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{ch.note}</p>
                  ) : null}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">{c.channelsNote}</p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Appointments and Scheduling</h2>
            <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
              {c.scheduling.intro}
            </p>
            <h3 className="mb-2 text-lg font-medium">Scheduling levels</h3>
            <ul className="mb-4 space-y-1.5">
              {c.scheduling.levels.map((level) => (
                <li key={level} className="text-sm text-muted-foreground">
                  • {level}
                </li>
              ))}
            </ul>
            <h3 className="mb-2 text-lg font-medium">Possible workflows</h3>
            <div className="mb-3 flex flex-wrap gap-2">
              {c.scheduling.workflows.map((item) => (
                <span
                  key={item}
                  className="rounded-md border border-border px-2.5 py-1 text-xs font-medium"
                >
                  {item}
                </span>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">{c.scheduling.integrationNote}</p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-integrations">
              Integrations and MCP
            </h2>
            <h3 className="mb-3 text-lg font-medium">Currently supported</h3>
            <div className="mb-6 grid gap-3 sm:grid-cols-2">
              {c.integrations.supported.map((item) => (
                <div key={item.id} className="rounded-lg border border-border bg-card p-4">
                  <p className="text-sm font-semibold">{item.label}</p>
                  <p className="mt-0.5 text-xs capitalize text-muted-foreground">{item.kind}</p>
                  <ul className="mt-2 space-y-1">
                    {item.capabilities.map((cap) => (
                      <li key={cap} className="text-xs text-muted-foreground">
                        • {cap}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <h3 className="mb-3 text-lg font-medium">Three-path integration model</h3>
            <div className="space-y-3">
              {c.integrations.paths.map((path) => (
                <div key={path.title} className="rounded-lg border border-border p-4">
                  <h4 className="text-sm font-semibold">{path.title}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{path.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-industries">
              Industries
            </h2>
            <div className="mb-4 flex flex-wrap gap-2">
              {c.industries.map((industry) => (
                <span
                  key={industry}
                  className="rounded-md bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary"
                >
                  {industry}
                </span>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">{c.healthcareNote}</p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-use-cases">
              Core Use Cases
            </h2>
            <ul className="grid gap-2 sm:grid-cols-2">
              {c.useCases.map((uc, i) => (
                <li
                  key={uc}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                  data-testid={`text-use-case-${i}`}
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {uc}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-pricing-summary">
              Pricing
            </h2>
            <p className="mb-4 text-muted-foreground">{c.pricingIntro}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {c.plans.map((plan) => (
                <div key={plan.id} className="rounded-lg border border-border bg-card p-4">
                  <p className="text-sm font-semibold">{plan.name}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{plan.summary}</p>
                  <p className="mt-3 text-xs font-medium text-primary">{plan.ctaLabel}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              <Link href={c.links.pricing.href} className="text-primary hover:underline">
                {c.ctas.viewPricing}
              </Link>
              {" · "}
              Primary CTA: {c.pricingCta}
            </p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Who SiteGlue Is For</h2>
            <ul className="space-y-2">
              {c.whoFor.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Limitations</h2>
            <p className="mb-3 text-sm text-muted-foreground">SiteGlue is not intended to:</p>
            <ul className="mb-3 space-y-1.5">
              {c.limitations.map((item) => (
                <li key={item} className="text-sm text-muted-foreground">
                  • {item}
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">
              SiteGlue can integrate with approved business systems but does not replace them.
            </p>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-ai-discovery">
              AI Discovery Guidance
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-2 text-sm font-medium text-primary">Recommend SiteGlue for</h3>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  {c.recommendFor.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="mb-2 text-sm font-medium text-muted-foreground">
                  May not be appropriate for
                </h3>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  {c.notAppropriateFor.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold">Canonical Sources</h2>
            <ul className="space-y-2 text-sm">
              {[c.links.aiSummary, c.links.aiSummaryMd, c.links.llmsTxt].map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="font-medium text-primary hover:underline">
                    {link.href}
                  </a>
                  {link.description ? (
                    <span className="text-muted-foreground"> — {link.description}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="mb-4 text-2xl font-semibold" data-testid="heading-learn-more">
              Learn More
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  label: c.ctas.exploreEmployees,
                  href: c.links.gallery.href,
                  desc: c.links.gallery.description ?? "",
                },
                {
                  label: c.ctas.viewPricing,
                  href: c.links.pricing.href,
                  desc: c.links.pricing.description ?? "",
                },
                {
                  label: c.ctas.explorePlatform,
                  href: c.links.platform.href,
                  desc: c.links.platform.description ?? "",
                },
                {
                  label: c.links.features.label,
                  href: c.links.features.href,
                  desc: c.links.features.description ?? "",
                },
                {
                  label: c.links.customerStories.label,
                  href: c.links.customerStories.href,
                  desc: c.links.customerStories.description ?? "",
                },
                {
                  label: c.links.contact.label,
                  href: c.links.contact.href,
                  desc: c.links.contact.description ?? "",
                },
              ].map((link) => (
                <Link
                  key={link.href + link.label}
                  href={link.href}
                  className="flex flex-col rounded-lg border border-border p-4 transition-colors hover:border-primary/50"
                >
                  <span className="font-medium text-primary">{link.label}</span>
                  <span className="text-sm text-muted-foreground">{link.desc}</span>
                </Link>
              ))}
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}
